import { Skeleton } from "antd";
import React, { useContext, useEffect } from "react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { StateContext } from "../../../store/context";

interface CompareResultsDynamic {
  convertedOpen: string;
  convertedLastTradedPrice: string;
  PELTM: string;
  LTM: string;
  PEBySector: string;
  fairValue: string;
  estimatedFairValue: string;
  netAssetPerShare: string;
  priceToBookValue: string;
  marketCap: number;
}

interface CompareResults {
  dynamic: CompareResultsDynamic;
  grossMarginAvg: string;
  netMarginAvg: string;
}

interface PrimaryStock {
  last_traded_date: string;
  whitelisted: boolean;
  compareResults: CompareResults;
}

interface StockComparisonProps {
  primaryStock: PrimaryStock;
  primaryData: any;
  compareOtherStockResults: {
    stocks: Array<{
      compareResults: CompareResults;
      details: PrimaryStock;
    }>;
  };
  peEstimate?: string;
  compareLoading?: boolean;
  scrollDirection: "left" | "right";
  scrollUpdate: boolean;
  selectedStocks: any[];
  mobileScrollIndex?: number;
  isMobile?: boolean;
}

export const StockComparisonTable: React.FC<StockComparisonProps> = ({
  primaryStock,
  primaryData,
  compareOtherStockResults,
  peEstimate,
  compareLoading = false,
  scrollDirection,
  scrollUpdate,
  selectedStocks,
  mobileScrollIndex = 0,
  isMobile = false,
}) => {
  const [scrollIndex, setScrollIndex] = React.useState(0);
  // Every column (label + primary + each secondary stock) renders the
  // same `parameters` array independently as its own flex-column DOM
  // subtree, so there's no single <tr> to hover — tracking the hovered
  // index here and having each column check it against its own row
  // gives the same "whole row lights up" effect across all of them.
  const [hoveredRow, setHoveredRow] = React.useState<number | null>(null);
  const rowBackground = (index: number) =>
    hoveredRow === index
      ? "var(--vi-surface-hover)"
      : index % 2 === 1
      ? "color-mix(in srgb, var(--vi-text-tertiary) 6%, transparent)"
      : "transparent";
  const rowHoverProps = (index: number) => ({
    onMouseEnter: () => setHoveredRow(index),
    onMouseLeave: () =>
      setHoveredRow((prev) => (prev === index ? null : prev)),
  });
  const itemWidth = isMobile ? 200 : 200;
  const visibleItems = 0;
  const { baseCurrency } = useContext(StateContext);
  const touchStartRef = React.useRef<{ x: number; y: number } | null>(null);
  const formatNumber = (
    value: string | undefined,
    decimals: number = 2
  ): string => {
    if (!value || isNaN(parseFloat(value))) return "-";
    return parseFloat(value).toFixed(decimals);
  };

  const currencySymbols: any = {
    INR: "₹",
    LKR: "Rs",
    USD: "$",
    QAR: "﷼",
    AED: "د.إ",
    SAR: "﷼",
  };

  const LoadingSkeleton: React.FC = () => (
    <div className="flex items-center justify-end w-full">
      <Skeleton.Input active className="w-20 h-6" size="small" />
    </div>
  );

  const LoadingStockColumn: React.FC = () => (
    <div className="w-[190px] px-4 flex-shrink-0">
      {parameters?.map((param, index) => (
        <div
          key={`loading-${param.key}`}
          className="h-9 flex items-center justify-end text-sm text-gray-600 rounded-md px-1"
          style={{ background: rowBackground(index) }}
        >
          <LoadingSkeleton />
        </div>
      ))}
    </div>
  );

  const renderValue = (
    value: string | number | undefined,
    formatter: (val: string | undefined) => string,
    isLoading: boolean
  ) => {
    if (isLoading) return <LoadingSkeleton />;
    if (value === undefined || value === null) return "-";
    return formatter(value.toString());
  };

  const parameters = [
    {
      label: "Open",
      key: "convertedOpen",
      getValue: (stock: any) => stock?.dynamic?.convertedOpen,
      decimals: 4,
    },
    {
      label: "Last traded date",
      key: "last_traded_date",
      getValue: (stock: any) => {
        if (stock === primaryData) {
          return primaryStock?.last_traded_date;
        }
        return stock.details?.last_traded_date;
      },
      formatter: (val: string | undefined) => val || "-",
    },
    {
      label: "Last traded price",
      key: "convertedLastTradedPrice",
      getValue: (stock: any) => stock?.dynamic?.convertedLastTradedPrice,
      decimals: 4,
    },
    {
      label: "TTM EPS",
      key: "LTM",
      getValue: (stock: any) => stock?.dynamic?.LTM,
      // shouldShow: (val: string | undefined) => val && parseFloat(val) > 0, TODO: please handle this properly.
      decimals: 4,
    },
    {
      label: "PE Ratio",
      key: "PELTM",
      getValue: (stock: any) => stock?.dynamic?.PELTM,
      //shouldShow: (val: string | undefined) => val && parseFloat(val) > 0,
      formatter: (val: string | undefined) => {
        const num = Number(val);

        if (isNaN(num)) {
          return "NaN";
        } else if (num > 50) {
          return ">50";
        } else if (num >= 0 && num <= 50) {
          return formatNumber(val);
        } else {
          return "NaN";
        }
      },
    },
    {
      label: "Sector PE",
      key: "PEBySector",
      getValue: (stock: any) => stock?.dynamic?.PEBySector,
      shouldShow: (val: string | undefined) => val && parseFloat(val) > 0,
    },
    {
      label: "Net asset per share",
      key: "netAssetPerShare",
      getValue: (stock: any) => stock?.dynamic?.netAssetPerShare,
      decimals: 4,
    },
    {
      label: "Return On Equity",
      key: "returnOnEquityLTM",
      getValue: (stock: any) => stock?.dynamic?.returnOnEquityLTM,
      formatter: (val: string | undefined) =>
        val ? `${formatNumber(val)}%` : "-",
    },
    {
      label: "Price to Book",
      key: "priceToBookValue",
      getValue: (stock: any) => stock?.dynamic?.priceToBookValue,
    },
    {
      label: "TTM Gross Margin",
      key: "grossMarginAvg",
      getValue: (stock: any) => stock?.dynamic?.TTMGrossProfitMargin,
      formatter: (val: string | undefined) =>
        val ? `${formatNumber(val)}%` : "-",
    },
    {
      label: "TTM Net Margin",
      key: "netMarginAvg",
      getValue: (stock: any) => stock?.dynamic?.TTMNetProfitMargin,
      formatter: (val: string | undefined) =>
        val ? `${formatNumber(val)}%` : "-",
    },
    {
      label: "TTM Dividend Yield",
      key: "dividendYieldLTM",
      getValue: (stock: any) => stock?.dynamic?.dividendYieldLTM,
      formatter: (val: string | undefined) =>
        val ? `${formatNumber(val)}%` : "-",
    },
    {
      label: "TTM Revenue",
      key: "revenueLTM",
      getValue: (stock: any) => `  ${stock?.dynamic?.revenueLTM?.toString()}`,
      shouldShow: (val: string | undefined) =>
        val !== undefined && val !== null,
      formatter: (val: string | undefined) => {
        if (!val || isNaN(Number(val))) return "-";

        const num = Number(val);
        const currencySymbol = currencySymbols[baseCurrency] || "?";

        if (num >= 1_000_000_000) {
          return `${currencySymbol}. ${(num / 1_000_000_000).toFixed(2)} B`;
        } else if (num >= 1_000_000) {
          return `${currencySymbol} ${(num / 1_000_000).toFixed(2)} M`;
        } else {
          return `${currencySymbol}. ${num.toLocaleString("en-US", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })}`;
        }
      },
    },

    {
      label: "Market Capitalization",
      key: "marketCap",
      getValue: (stock: any) => `  ${stock?.dynamic?.marketCap?.toString()}`,
      shouldShow: (val: string | undefined) =>
        val !== undefined && val !== null,
      formatter: (val: string | undefined) => {
        if (!val || isNaN(Number(val))) return "-";

        const num = Number(val);
        const currencySymbol = currencySymbols[baseCurrency] || "?";

        if (num >= 1_000_000_000) {
          return `${currencySymbol}. ${(num / 1_000_000_000).toFixed(2)} B`;
        } else if (num >= 1_000_000) {
          return `${currencySymbol} ${(num / 1_000_000).toFixed(2)} M`;
        } else {
          return `${currencySymbol}. ${num.toLocaleString("en-US", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })}`;
        }
      },
    },
  ];

  if (peEstimate) {
    parameters.push(
      {
        label: `Fair value at PE ${peEstimate}`,
        key: "fairValue",
        getValue: (stock: any) => stock?.dynamic?.fairValue?.toFixed(2),
        shouldShow: (val: string | undefined) =>
          val !== undefined && val !== null,
      },
      {
        label: `Fair value with EPS estimate at PE ${peEstimate}`,
        key: "estimatedFairValue",
        getValue: (stock: any) =>
          stock?.dynamic?.estimatedFairValue?.toFixed(2),
        shouldShow: (val: string | undefined) =>
          val !== undefined && val !== null,
      }
    );
  }

  const canScrollLeft = scrollIndex > 0;
  const canScrollRight =
    scrollIndex + visibleItems < compareOtherStockResults?.stocks?.length;

  const handleScroll = (direction: "left" | "right") => {
    if (direction === "left" && canScrollLeft) {
      setScrollIndex((prev) => prev - 1);
    } else if (direction === "right" && canScrollRight) {
      setScrollIndex((prev) => prev + 1);
    }
  };

  useEffect(() => {
    // Only handle scroll for desktop devices
    if (!isMobile) {
      handleScroll(scrollDirection);
    }
  }, [scrollDirection, scrollUpdate, isMobile]);

  function formatToCurrency(value: any, decimals: number = 2) {
    if (value === null || value === undefined || isNaN(Number(value))) {
      return "-";
    }

    // Ensure the value is converted to a number
    const numericValue = Number(value);

    // Format number with commas and two decimal places
    return numericValue.toLocaleString("en-US", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  }

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
        e.preventDefault();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const handleContainerTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      const touch = e.touches[0];
      touchStartRef.current = { x: touch.clientX, y: touch.clientY };
    }
  };

  const handleContainerTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 1 && touchStartRef.current) {
      const touch = e.touches[0];
      const deltaX = Math.abs(touch.clientX - touchStartRef.current.x);
      const deltaY = Math.abs(touch.clientY - touchStartRef.current.y);

      // If horizontal movement is greater than vertical, prevent it
      if (deltaX > deltaY && deltaX > 5) {
        e.preventDefault();
      }
    }
  };

  const handleContainerTouchEnd = () => {
    touchStartRef.current = null;
  };

  return (
    <div
      className="vi-glass-card p-5 relative overflow-x-hidden"
      onTouchStart={handleContainerTouchStart}
      onTouchMove={handleContainerTouchMove}
      onTouchEnd={handleContainerTouchEnd}
    >
      <div className="flex overflow-x-hidden">
        {/* Parameters Column - Always Fixed */}
        <div className="min-w-[120px] md:min-w-[180px]">
          {parameters.map((param, index) => (
            <div
              key={param.label}
              className="h-9 flex items-center font-normal text-xs md:text-sm text-[#667085] font-inter rounded-md px-1"
              style={{ background: rowBackground(index) }}
              {...rowHoverProps(index)}
            >
              {param.label}
            </div>
          ))}
        </div>

        {/* Primary Stock Column - Fixed on Desktop, Scrollable on Mobile */}
        {!isMobile && (
          <div className="w-[120px] md:w-[180px] px-4">
            <div className="w-[120px] md:w-[180px] px-4  flex-shrink-0">
              {compareLoading ? (
                <LoadingStockColumn />
              ) : (
                <>
                  {parameters.map((param, index) => {
                    const value = param.getValue(primaryData);
                    if (param.shouldShow && !param.shouldShow(value)) {
                      return (
                        <div
                          key={`${param.key}-primary`}
                          className="h-9 text-xs md:text-sm rounded-md"
                          style={{ background: rowBackground(index) }}
                          {...rowHoverProps(index)}
                        />
                      );
                    }
                    return (
                      <div
                        key={`${param.key}-primary`}
                        className="h-9 flex items-center relative justify-center text-nowrap md:justify-end text-xs md:text-sm text-[#101828] font-inter tabular-nums rounded-md px-1"
                        style={{ background: rowBackground(index) }}
                        {...rowHoverProps(index)}
                      >
                        {renderValue(
                          value,
                          param.formatter ||
                            ((val: string | undefined) =>
                              formatToCurrency(val, param.decimals)),
                          false
                        )}
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          </div>
        )}

        {/* Scrollable Content - Includes Primary Stock on Mobile */}
        <div className="relative flex-1">
          <div className="overflow-hidden">
            <div
              className="flex transition-transform duration-300 font-inter w-full  overflow-hidden"
              style={{
                transform: isMobile
                  ? `translateX(-${mobileScrollIndex * 200}px)` // 80px (mobile primary) + 190px (secondary) = 270px per step
                  : `translateX(-${scrollIndex * itemWidth}px)`,
              }}
            >
              {/* Primary Stock Column - Only on Mobile */}
              {isMobile && (
                <div className="w-[80px]  md:px-4 flex-shrink-0">
                  {compareLoading ? (
                    <LoadingStockColumn />
                  ) : (
                    <>
                      {parameters.map((param, index) => {
                        const value = param.getValue(primaryData);
                        if (param.shouldShow && !param.shouldShow(value)) {
                          return (
                            <div
                              key={`${param.key}-primary-mobile`}
                              className="h-9 text-xs md:text-sm rounded-md"
                              style={{ background: rowBackground(index) }}
                              {...rowHoverProps(index)}
                            />
                          );
                        }
                        return (
                          <div
                            key={`${param.key}-primary-mobile`}
                            className="h-9 flex items-center justify-end font-inter text-xs md:text-sm text-[#101828] tabular-nums rounded-md px-1"
                            style={{ background: rowBackground(index) }}
                            {...rowHoverProps(index)}
                          >
                            {renderValue(
                              value,
                              param.formatter ||
                                ((val: string | undefined) =>
                                  formatToCurrency(val, param.decimals)),
                              false
                            )}
                          </div>
                        );
                      })}
                    </>
                  )}
                </div>
              )}

              {/* Secondary Stocks */}
              {compareLoading
                ? selectedStocks?.map((_, index) => (
                    <LoadingStockColumn key={`loading-stock-${index}`} />
                  ))
                : compareOtherStockResults?.stocks?.map((stock, stockIndex) => (
                    <div
                      key={stockIndex}
                      className="px-2 font-inter text-xs md:text-sm flex-shrink-0 border-l border-[#EAECF0]"
                      style={{
                        width: isMobile ? "200px" : "200px",
                      }}
                    >
                      {parameters.map((param, index) => {
                        const value = param.getValue(stock);
                        if (param.shouldShow && !param.shouldShow(value)) {
                          return (
                            <div
                              key={`${param.key}-${stockIndex}`}
                              className="h-9 rounded-md"
                              style={{ background: rowBackground(index) }}
                              {...rowHoverProps(index)}
                            />
                          );
                        }
                        return (
                          <div
                            key={`${param.key}-${stockIndex}`}
                            className="h-9 flex items-center justify-end font-inter text-xs md:text-sm text-[#101828] tabular-nums rounded-md px-1"
                            style={{ background: rowBackground(index) }}
                            {...rowHoverProps(index)}
                          >
                            {renderValue(
                              value,
                              param.formatter ||
                                ((val: string | undefined) =>
                                  formatToCurrency(val, param.decimals)),
                              false
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
